// src/production/Main_Dashboard.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList
} from "recharts";
import {
  Gem,
  CheckCircle2,
  Layers,
  Hammer,
  FileText,
  Sparkles,
  ShieldCheck,
  ChevronRight,
  Bell,
  UserCircle,
  TrendingUp,
  TrendingDown,
  Box,
  ChevronDown,
  AlertCircle,
  BarChart3,
  LogOut,
  Clock,
  AlertTriangle,
  X,
  Activity,
  Trophy,
  Inbox,
  Wrench
} from 'lucide-react';
import { getStoredUser, setAuthToken, productionApi } from "../api";
import SkeletonLoader from './components/SkeletonLoader';
import ApiButton from './components/ApiButton';
import { BREAKPOINTS } from './productionTheme';

// --- SHARED UI COMPONENTS ---
const NavCard = ({ title, description, icon: Icon, color, stats, onClick, badge }) => (
  <button
    onClick={onClick}
    className="group relative bg-white rounded-3xl border border-slate-200 p-8 text-left transition-all duration-300 hover:shadow-2xl hover:shadow-slate-200 hover:-translate-y-2 overflow-hidden"
  >
    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 ${color.bg} ${color.text} group-hover:scale-110 group-hover:rotate-3`}>
      <Icon size={28} />
    </div>
    <div className="flex items-center gap-2 mb-2">
      <h3 className="text-xl font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">{title}</h3>
      {badge > 0 && (
        <span className="bg-red-500 text-white rounded-full px-2 py-0.5 text-xs font-black">
          {badge}
        </span>
      )}
    </div>
    <p className="text-slate-500 text-sm leading-relaxed mb-8">{description}</p>
    <div className="flex items-center justify-between pt-6 border-t border-slate-50">
      <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">{stats}</span>
      <div className="flex items-center text-indigo-600 font-bold text-sm opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
        Open <ChevronRight size={16} />
      </div>
    </div>
  </button>
);

const formatStageLabel = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const ThroughputTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const today = Number(payload.find((entry) => entry.dataKey === 'today_count')?.value || 0);
  const week = Number(payload.find((entry) => entry.dataKey === 'week_count')?.value || 0);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white/95 px-4 py-3 shadow-2xl shadow-slate-200 backdrop-blur">
      <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">{formatStageLabel(label)}</div>
      <div className="mt-3 space-y-2">
        <div className="flex items-center justify-between gap-8 text-sm">
          <span className="font-semibold text-slate-500">Today</span>
          <span className="font-black text-indigo-600">{today}</span>
        </div>
        <div className="flex items-center justify-between gap-8 text-sm">
          <span className="font-semibold text-slate-500">This Week</span>
          <span className="font-black text-cyan-600">{week}</span>
        </div>
        <div className="border-t border-slate-100 pt-2 text-xs font-semibold text-slate-500">
          Daily share: <span className="text-slate-900">{week > 0 ? `${Math.round((today / week) * 100)}%` : '0%'}</span>
        </div>
      </div>
    </div>
  );
};

export default function MainDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [alertPanelOpen, setAlertPanelOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [overdueCount, setOverdueCount] = useState(0);
  const [activeView, setActiveView] = useState('home'); // 'home' | 'delays'
  const [delayReport, setDelayReport] = useState([]);
  const [delayLoading, setDelayLoading] = useState(false);

  // Real KPI state
  const [kpis, setKpis] = useState(null);
  const [throughput, setThroughput] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inboxCount, setInboxCount] = useState(0);
  const [reworkCount, setReworkCount] = useState(0);
  const currentUser = getStoredUser() || {};
  const displayName = [
    currentUser.first_name,
    currentUser.last_name,
  ].filter(Boolean).join(' ').trim()
    || currentUser.name
    || currentUser.full_name
    || currentUser.username
    || 'Production User';
  const displayRole = currentUser.role || currentUser.user_role || 'Production';

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Poll for overdue stages every 5 minutes
  const checkOverdue = async () => {
    try {
      const res = await productionApi.get('/stages/check-overdue');
      setAlerts(res.data.unread_alerts || []);
      setOverdueCount(res.data.overdue_count || 0);
    } catch (e) {
      // silently ignore — user may not have started any stages
    }
  };

  // Load KPIs + metal loss on mount
  useEffect(() => {
    checkOverdue();
    const interval = setInterval(checkOverdue, 5 * 60 * 1000);

    const loadDashboardData = async () => {
      setLoading(true);
      try {
        const [kpiResult, throughputResult] = await Promise.allSettled([
          productionApi.get('/dashboard/production-kpis'),
          productionApi.get('/dashboard/stage-throughput'),
        ]);

        if (kpiResult.status === 'fulfilled') {
          const payload = kpiResult.value?.data || {};
          const hasKpiShape = (
            payload.active_job_cards !== undefined ||
            payload.stages_completed_today !== undefined ||
            payload.qc_pending_count !== undefined
          );
          if (payload.success || hasKpiShape) {
            setKpis(payload);
          } else {
            console.error('KPI payload invalid:', payload);
            setKpis({
              active_job_cards: 0,
              stages_completed_today: 0,
              qc_pending_count: 0,
              on_time_rate: 0,
              metal_loss_today_grams: 0,
              rework_open: 0
            });
          }
        } else {
          console.error('KPI load failed:', kpiResult.status === 'rejected' ? kpiResult.reason : kpiResult.value?.data);
          setKpis({
            active_job_cards: 0,
            stages_completed_today: 0,
            qc_pending_count: 0,
            on_time_rate: 0,
            metal_loss_today_grams: 0,
            rework_open: 0
          });
        }

        if (throughputResult.status === 'fulfilled' && throughputResult.value?.data?.success) {
          setThroughput(throughputResult.value.data.stages || []);
        } else {
          console.error('Throughput load failed:', throughputResult.status === 'rejected' ? throughputResult.reason : throughputResult.value?.data);
          setThroughput([]);
        }
      } catch (e) {
        console.error('Dashboard load error:', e);
        setKpis({
          active_job_cards: 0,
          stages_completed_today: 0,
          qc_pending_count: 0,
          on_time_rate: 0,
          metal_loss_today_grams: 0,
          rework_open: 0
        });
        setThroughput([]);
      } finally {
        setLoading(false);
      }
    };
    loadDashboardData();

    // Load orders inbox count for badge
    productionApi.get('/sales/orders/production-inbox?status=SENT_TO_PRODUCTION')
      .then(r => setInboxCount(r.data.awaiting_review_count || 0))
      .catch(() => {});

    // Load rework queue count for badge
    productionApi.get('/production/rework-queue')
      .then(r => setReworkCount(r.data.total || 0))
      .catch(() => {});

    return () => clearInterval(interval);
  }, []);

  const markAlertRead = async (alertId) => {
    try {
      await productionApi.post(`/stages/alerts/${alertId}/read`);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (e) { }
  };

  const fetchDelayReport = async () => {
    setDelayLoading(true);
    try {
      const res = await productionApi.get('/reports/stage-delays');
      if (Array.isArray(res.data)) {
        setDelayReport(res.data);
      } else {
        setDelayReport(res.data?.stages || []);
      }
    } catch (e) {
      setDelayReport([]);
    } finally {
      setDelayLoading(false);
    }
  };

  const openDelaysView = () => {
    setActiveView('delays');
    fetchDelayReport();
  };

  const handleLogout = () => {
    setAuthToken(null);   // removes accessToken + axios header
    onLogout();          // calls App.js logout logic
  };

  const hasMeaningfulKpiData = !!kpis && (
    Number(kpis.active_job_cards || 0) > 0 ||
    Number(kpis.stages_completed_today || 0) > 0 ||
    Number(kpis.qc_pending_count || 0) > 0 ||
    Number(kpis.on_time_rate || 0) > 0 ||
    Number(kpis.metal_loss_today_grams || 0) > 0 ||
    Number(kpis.rework_open || 0) > 0
  );

  const throughputChartData = useMemo(() => (
    (throughput || [])
      .slice(0, 12)
      .map((stage) => ({
        ...stage,
        stage_label: formatStageLabel(stage.stage_code),
        today_count: Number(stage.today_count || 0),
        week_count: Number(stage.week_count || 0),
      }))
      .sort((a, b) => Number(b.week_count || 0) - Number(a.week_count || 0))
  ), [throughput]);

  const throughputSummary = useMemo(() => {
    const totals = throughputChartData.reduce((acc, stage) => {
      acc.today += stage.today_count || 0;
      acc.week += stage.week_count || 0;
      return acc;
    }, { today: 0, week: 0 });

    const topStage = throughputChartData[0] || null;
    const mostActiveToday = [...throughputChartData].sort((a, b) => (b.today_count || 0) - (a.today_count || 0))[0] || null;

    return {
      totalToday: totals.today,
      totalWeek: totals.week,
      topStage,
      mostActiveToday,
    };
  }, [throughputChartData]);

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <style>{`
        @media (max-width: ${BREAKPOINTS.mobile}) {
          .production-kpi-grid { grid-template-columns: 1fr !important; }
          .production-nav-grid { grid-template-columns: 1fr !important; }
          .production-main-padding { padding: 16px !important; }
        }
        @media (min-width: ${BREAKPOINTS.mobile}) and (max-width: ${BREAKPOINTS.desktop}) {
          .production-kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .production-nav-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
      `}</style>
      {/* Top Simple Header */}
      <nav className="h-20 bg-white border-b border-slate-200 px-8 flex items-center justify-between sticky top-0 z-40 shadow-sm">
        <div className="flex items-center gap-4">
          <div
            onClick={() => navigate("/production")}
            className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white cursor-pointer shadow-lg shadow-indigo-200 transition-transform hover:scale-105 active:scale-95"
          >
            <Sparkles size={24} />
          </div>
          <span className="text-2xl font-black text-slate-900 tracking-tighter uppercase italic">ZILVERRA<span className="text-indigo-600">JEWELS</span></span>
        </div>

        <div className="flex items-center gap-6">
          <div className="hidden md:flex bg-slate-100 p-1 rounded-full border border-slate-200">
            <button
              onClick={() => setActiveView('home')}
              className={`px-5 py-2 rounded-full text-xs font-bold transition-all ${activeView === 'home' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-500 hover:text-slate-800'}`}
            >
              Dashboard
            </button>
            <button
              onClick={openDelaysView}
              className={`px-5 py-2 rounded-full text-xs font-bold transition-all ${activeView === 'delays' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-500 hover:text-slate-800'}`}
            >
              Delay Report
            </button>
          </div>
          <div className="h-8 w-px bg-slate-200"></div>

          {/* Alert Bell */}
          <div className="relative">
            <button
              onClick={() => setAlertPanelOpen(p => !p)}
              className="relative p-2 rounded-xl hover:bg-slate-100 transition-colors"
            >
              <Bell size={20} className={alerts.length > 0 ? 'text-red-500' : 'text-slate-500'} />
              {alerts.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center border-2 border-white">
                  {alerts.length > 9 ? '9+' : alerts.length}
                </span>
              )}
            </button>

            {alertPanelOpen && (
              <div className="absolute right-0 mt-3 w-96 bg-white rounded-2xl border border-slate-200 shadow-2xl shadow-slate-200/60 z-50 overflow-hidden">
                <div className="px-4 py-3 bg-red-50 border-b border-red-100 flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={16} className="text-red-600" />
                    <span className="text-sm font-bold text-red-800">
                      Overdue Stages ({overdueCount})
                    </span>
                  </div>
                  <button onClick={() => setAlertPanelOpen(false)}>
                    <X size={16} className="text-slate-400 hover:text-slate-600" />
                  </button>
                </div>
                <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                  {alerts.length === 0 ? (
                    <div className="p-6 text-center text-slate-400 text-sm">
                      <CheckCircle2 size={32} className="mx-auto mb-2 text-emerald-400" />
                      All stages on time
                    </div>
                  ) : (
                    alerts.map(alert => (
                      <div key={alert.id} className="px-4 py-3 hover:bg-slate-50 flex gap-3 items-start">
                        <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${alert.severity === 'high' ? 'bg-red-500' : 'bg-amber-500'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-700 leading-relaxed">{alert.message}</p>
                          <p className="text-[10px] text-slate-400 mt-1">
                            {alert.created_at ? new Date(alert.created_at).toLocaleString() : ''}
                          </p>
                        </div>
                        <button
                          onClick={() => markAlertRead(alert.id)}
                          className="text-[10px] text-indigo-600 font-bold hover:text-indigo-800 flex-shrink-0"
                        >
                          Dismiss
                        </button>
                      </div>
                    ))
                  )}
                </div>
                {alerts.length > 0 && (
                  <div className="px-4 py-2 border-t border-slate-100 text-right">
                    <button
                      onClick={() => { alerts.forEach(a => markAlertRead(a.id)); }}
                      className="text-xs text-slate-500 hover:text-slate-700 font-medium"
                    >
                      Dismiss all
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="h-8 w-px bg-slate-200"></div>

          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className={`flex items-center gap-3 p-1 pr-3 rounded-2xl transition-all hover:bg-slate-50 ${isUserMenuOpen ? 'bg-slate-50 ring-2 ring-indigo-500/20' : ''}`}
            >
              <div className="w-10 h-10 rounded-2xl bg-slate-200 flex items-center justify-center text-slate-600 border-2 border-white shadow-sm overflow-hidden group">
                <UserCircle size={24} className="group-hover:scale-110 transition-transform" />
              </div>
              <div className="text-right hidden sm:block">
                <p className="text-xs font-bold text-slate-900">{displayName}</p>
                <p className="text-[10px] text-slate-400 uppercase font-bold tracking-widest">{displayRole}</p>
              </div>
              <ChevronDown size={14} className={`text-slate-400 transition-transform duration-300 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {isUserMenuOpen && (
              <div className="absolute right-0 mt-3 w-56 bg-white rounded-3xl border border-slate-200 shadow-2xl shadow-slate-200/50 py-3 z-50 animate-in fade-in zoom-in-95 duration-200 origin-top-right">
                <div className="px-4 py-2 mb-2 border-b border-slate-50">
                  <p className="text-xs font-bold text-slate-900 truncate">{displayName}</p>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{displayRole}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-rose-500 hover:bg-rose-50 transition-all"
                >
                  <LogOut size={18} />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      <main className="production-main-padding max-w-7xl mx-auto p-8 lg:p-12">

        {/* ========== HOME VIEW ========== */}
        {activeView === 'home' && (
          <div className="animate-in fade-in duration-700">
            <header className="mb-12">
              <h1 className="text-4xl font-black text-slate-900 tracking-tight">Production Hub</h1>
              <p className="text-slate-500 mt-2 text-lg font-medium">Select a module to manage your jewelry manufacturing flow.</p>
            </header>

            {(loading || hasMeaningfulKpiData) && (
              <div className="production-kpi-grid grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-10">
                {loading
                  ? <SkeletonLoader count={6} height={132} />
                  : [
                    { label: 'Job Cards Active', val: kpis?.active_job_cards ?? 0, icon: Box, color: 'text-blue-600', bg: 'bg-blue-50' },
                    { label: 'Stages Done Today', val: kpis?.stages_completed_today ?? 0, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    { label: 'QC Pending', val: kpis?.qc_pending_count ?? 0, icon: ShieldCheck, color: 'text-amber-600', bg: 'bg-amber-50' },
                    { label: 'On-Time Delivery %', val: `${kpis?.on_time_rate ?? 0}%`, icon: TrendingUp, color: 'text-teal-600', bg: 'bg-teal-50', progress: kpis?.on_time_rate ?? 0 },
                    { label: 'Metal Loss Today (g)', val: Number(kpis?.metal_loss_today_grams || 0).toFixed(3), icon: AlertTriangle, color: 'text-rose-600', bg: 'bg-rose-50' },
                    { label: 'Rework Items Open', val: kpis?.rework_open ?? 0, icon: Wrench, color: 'text-yellow-600', bg: 'bg-yellow-50' },
                  ].map((s, i) => (
                    <div key={i} className="group bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl hover:border-slate-200 hover:-translate-y-1 transition-all duration-300">
                      <div className="flex items-center gap-5">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-500 ${s.bg} ${s.color} group-hover:scale-110 group-hover:rotate-6`}>
                          <s.icon size={24} />
                        </div>
                        <div>
                          <div className="text-2xl font-black text-slate-900">{s.val}</div>
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">{s.label}</div>
                        </div>
                      </div>
                      {typeof s.progress === 'number' && (
                        <div className="mt-4">
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-teal-500 rounded-full transition-all duration-700"
                              style={{ width: `${Math.min(100, Math.max(0, s.progress))}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
            {/* Stage Throughput Chart */}
            <div className="relative overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm p-6 mb-12">
              <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-[radial-gradient(circle_at_top_left,_rgba(99,102,241,0.14),_transparent_42%),radial-gradient(circle_at_top_right,_rgba(6,182,212,0.14),_transparent_38%)]" />
              <div className="relative flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <BarChart3 size={20} className="text-indigo-500" /> Stage Throughput
                  </h2>
                  <p className="text-xs text-slate-400 mt-0.5">A compact view of daily and weekly stage completions.</p>
                </div>
                {throughputChartData.length > 0 && (
                  <div className="hidden md:flex items-center gap-3">
                    <div className="rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-right">
                      <div className="text-[10px] font-black uppercase tracking-[0.18em] text-indigo-400">Today</div>
                      <div className="text-2xl font-black text-indigo-700">{throughputSummary.totalToday}</div>
                    </div>
                    <div className="rounded-2xl border border-cyan-100 bg-cyan-50 px-4 py-3 text-right">
                      <div className="text-[10px] font-black uppercase tracking-[0.18em] text-cyan-500">This Week</div>
                      <div className="text-2xl font-black text-cyan-700">{throughputSummary.totalWeek}</div>
                    </div>
                  </div>
                )}
              </div>
              <div className="relative">
                {loading ? (
                  <div className="h-[21rem] bg-slate-100 rounded-2xl animate-pulse" />
                ) : throughputChartData.length === 0 ? (
                  <div className="h-[21rem] flex items-center justify-center text-sm text-slate-400 font-medium">
                    No stage throughput data for today/week.
                  </div>
                ) : (
                  <>
                    <div className="h-[21rem] rounded-[28px] border border-slate-100 bg-gradient-to-br from-slate-50 via-white to-cyan-50/50 p-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={throughputChartData}
                          layout="vertical"
                          barCategoryGap={12}
                          margin={{ top: 8, right: 24, left: 18, bottom: 8 }}
                        >
                          <defs>
                            <linearGradient id="throughputTodayGradient" x1="0" y1="0" x2="1" y2="0">
                              <stop offset="0%" stopColor="#4F46E5" />
                              <stop offset="100%" stopColor="#7C3AED" />
                            </linearGradient>
                            <linearGradient id="throughputWeekGradient" x1="0" y1="0" x2="1" y2="0">
                              <stop offset="0%" stopColor="#06B6D4" />
                              <stop offset="100%" stopColor="#0EA5E9" />
                            </linearGradient>
                          </defs>
                          <CartesianGrid horizontal={true} vertical={false} strokeDasharray="4 4" stroke="#E2E8F0" />
                          <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: '#64748B', fontSize: 11, fontWeight: 700 }} />
                          <YAxis
                            dataKey="stage_label"
                            type="category"
                            width={104}
                            tickLine={false}
                            axisLine={false}
                            tick={{ fill: '#0F172A', fontSize: 11, fontWeight: 700 }}
                          />
                          <Tooltip cursor={{ fill: 'rgba(148,163,184,0.08)' }} content={<ThroughputTooltip />} />
                          <Bar
                            dataKey="week_count"
                            name="This Week"
                            fill="url(#throughputWeekGradient)"
                            radius={[0, 10, 10, 0]}
                            barSize={14}
                            animationDuration={950}
                          >
                            <LabelList dataKey="week_count" position="right" className="fill-slate-500 font-bold text-[11px]" />
                          </Bar>
                          <Bar
                            dataKey="today_count"
                            name="Today"
                            fill="url(#throughputTodayGradient)"
                            radius={[0, 10, 10, 0]}
                            barSize={14}
                            animationDuration={1200}
                            animationBegin={160}
                          >
                            <LabelList dataKey="today_count" position="right" className="fill-slate-900 font-black text-[11px]" />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
                      <div className="rounded-2xl border border-slate-200 bg-slate-950 px-4 py-4 text-white shadow-inner shadow-slate-900/20">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Top Weekly Stage</div>
                            <div className="mt-1 text-base font-black">{throughputSummary.topStage?.stage_label || 'N/A'}</div>
                          </div>
                          <Activity size={18} className="text-cyan-300" />
                        </div>
                        <div className="mt-2 text-sm text-cyan-300">{throughputSummary.topStage?.week_count || 0} completed this week</div>
                      </div>
                      <div className="rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-4">
                        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-indigo-400">Most Active Today</div>
                        <div className="mt-1 text-base font-black text-slate-900">{throughputSummary.mostActiveToday?.stage_label || 'N/A'}</div>
                        <div className="mt-2 text-sm font-semibold text-indigo-700">{throughputSummary.mostActiveToday?.today_count || 0} completed today</div>
                      </div>
                      <div className="rounded-2xl border border-cyan-100 bg-cyan-50 px-4 py-4">
                        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-cyan-500">Today Vs Week</div>
                        <div className="mt-1 text-base font-black text-slate-900">
                          {throughputSummary.totalWeek > 0 ? `${Math.round((throughputSummary.totalToday / throughputSummary.totalWeek) * 100)}%` : '0%'}
                        </div>
                        <div className="mt-2 text-sm font-semibold text-cyan-700">
                          {throughputSummary.totalToday} today out of {throughputSummary.totalWeek} this week
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
                      {throughputChartData.slice(0, 4).map((stage, index) => {
                        const ratio = stage.week_count > 0 ? Math.min(100, Math.round((stage.today_count / stage.week_count) * 100)) : 0;
                        return (
                          <div
                            key={stage.stage_code}
                            className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                            style={{ animation: `fade-in 0.45s ease ${index * 0.08}s both` }}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-sm font-bold text-slate-900">{stage.stage_label}</div>
                              <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">{ratio}%</div>
                            </div>
                            <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
                              <div
                                className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-indigo-400 to-violet-400 transition-all duration-1000"
                                style={{ width: `${ratio}%` }}
                              />
                            </div>
                            <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                              <span>Today {stage.today_count}</span>
                              <span>Week {stage.week_count}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* Navigation Grid */}
            <div className="production-nav-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pb-12">
              <NavCard
                title="Master Design"
                description="Manage technical specifications, CAD library, and SKU details."
                icon={Gem}
                color={{ bg: 'bg-indigo-50', text: 'text-indigo-600' }}
                stats="450+ Active Designs"
                onClick={() => navigate("/production/master-design")}
              />
              <NavCard
                title="Job Card Creation"
                description="Generate new production orders."
                icon={FileText}
                color={{ bg: 'bg-blue-50', text: 'text-blue-600' }}
                stats="12 Pending Orders"
                onClick={() => navigate("/production/job-card")}
              />
              <NavCard
                title="Pre-Casting"
                description="Wax creation, tree assembly."
                icon={Layers}
                color={{ bg: 'bg-amber-50', text: 'text-amber-600' }}
                stats="8 Batches Ready"
                onClick={() => navigate("/production/pre-casting")}
              />
              <NavCard
                title="Post-Casting"
                description="Filing, grinding, bench work."
                icon={Hammer}
                color={{ bg: 'bg-rose-50', text: 'text-rose-600' }}
                stats="84 Units in Flow"
                onClick={() => navigate("/production/post-casting")}
              />
              <NavCard
                title="Final QC & Packing"
                description="Final inspection and dispatch."
                icon={ShieldCheck}
                color={{ bg: 'bg-emerald-50', text: 'text-emerald-600' }}
                stats="15 Ready to Ship"
                onClick={() => navigate("/production/final-qc")}
              />
              <NavCard
                title="QC Dashboard"
                description="Inspector workload, pass/fail trends, rework aging & queue heatmap."
                icon={Activity}
                color={{ bg: 'bg-purple-50', text: 'text-purple-600' }}
                stats="Live QC Analytics"
                onClick={() => navigate("/production/qc-dashboard")}
              />
              <NavCard
                title="Rework Queue"
                description="Pick up, complete, or scrap QC-rejected pieces. Track rework age and status."
                icon={Wrench}
                color={{ bg: 'bg-orange-50', text: 'text-orange-600' }}
                stats="Production Rework"
                badge={reworkCount}
                onClick={() => navigate("/production/rework-queue")}
              />
              <NavCard
                title="Worker Performance"
                description="Stars, KPIs, leaderboard & daily activity tracking."
                icon={Trophy}
                color={{ bg: 'bg-amber-50', text: 'text-amber-600' }}
                stats="Track All Workers"
                onClick={() => navigate("/production/worker-performance")}
              />
              <NavCard
                title="Worker Report"
                description="Search workers & view full activity, attendance & payroll reports."
                icon={FileText}
                color={{ bg: 'bg-blue-50', text: 'text-blue-600' }}
                stats="Appraisals & Reports"
                onClick={() => navigate("/production/worker-report")}
              />
              <NavCard
                title="Job Card Gantt"
                description="View production pipeline as an interactive Gantt timeline."
                icon={BarChart3}
                color={{ bg: 'bg-slate-100', text: 'text-slate-700' }}
                stats="Track Timelines"
                onClick={() => navigate("/production/gantt")}
              />
              <NavCard
                title="Orders Inbox"
                description="Confirmed sales orders waiting for production. Create or link job cards."
                icon={Inbox}
                color={{ bg: 'bg-rose-50', text: 'text-rose-600' }}
                stats="Incoming Orders"
                badge={inboxCount}
                onClick={() => navigate("/production/orders-inbox")}
              />
              <NavCard
                title="Goals & Tasks"
                description="Track production targets, update goal progress, and manage tasks from one easy workspace."
                icon={CheckCircle2}
                color={{ bg: 'bg-emerald-50', text: 'text-emerald-600' }}
                stats="Production Targets"
                onClick={() => navigate("/production/goals")}
              />
            </div>
          </div>
        )}

        {/* ========== STAGE DELAYS REPORT VIEW ========== */}
        {activeView === 'delays' && (
          <div className="animate-in fade-in duration-500">
            <header className="mb-8 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <button
                    onClick={() => setActiveView('home')}
                    className="text-slate-400 hover:text-slate-700 transition-colors text-sm font-medium"
                  >
                    ← Dashboard
                  </button>
                  <span className="text-slate-300">/</span>
                  <span className="text-sm font-bold text-indigo-600">Stage Delays Report</span>
                </div>
                <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                  <Activity size={28} className="text-indigo-500" />
                  Stage Delays Report
                </h1>
                <p className="text-slate-500 mt-1 text-sm font-medium">
                  Average expected vs. actual duration per manufacturing stage — identifies SLA bottlenecks.
                </p>
              </div>
              <ApiButton
                onClick={fetchDelayReport}
                loading={delayLoading}
                className="px-5 py-2.5 rounded-xl text-sm font-bold"
              >
                <span className="inline-flex items-center gap-2">
                  <Activity size={15} />
                  Refresh
                </span>
              </ApiButton>
            </header>

            {/* Summary KPI Strip */}
            {!delayLoading && delayReport.length > 0 && (() => {
              const totalStages = delayReport.length;
              const badStages = delayReport.filter(r => r.on_time_pct < 80).length;
              const avgGlobalDelay = (delayReport.reduce((a, r) => a + r.avg_delay_mins, 0) / totalStages).toFixed(1);
              const worstStage = delayReport[0]; // sorted by avg_delay desc from backend
              return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  {[
                    { label: 'Total Stages', val: totalStages, icon: Layers, color: 'text-indigo-600', bg: 'bg-indigo-50' },
                    { label: 'Below 80% SLA', val: badStages, icon: AlertTriangle, color: 'text-rose-600', bg: 'bg-rose-50' },
                    { label: 'Avg Global Delay', val: `${avgGlobalDelay}m`, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
                    { label: 'Worst Stage', val: worstStage.stage_code || '—', icon: AlertCircle, color: 'text-rose-600', bg: 'bg-rose-50' },
                  ].map((s, i) => (
                    <div key={i} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.bg} ${s.color}`}>
                        <s.icon size={20} />
                      </div>
                      <div>
                        <div className="text-xl font-black text-slate-900">{s.val}</div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{s.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Table */}
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <BarChart3 size={16} className="text-indigo-400" />
                  Delay Breakdown by Stage
                </h2>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  {delayReport.length} stages
                </span>
              </div>

              {delayLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-400">
                  <Activity size={32} className="animate-spin text-indigo-400" />
                  <span className="text-sm font-medium">Fetching stage data…</span>
                </div>
              ) : delayReport.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-400">
                  <CheckCircle2 size={40} className="text-emerald-400" />
                  <span className="text-sm font-medium">No completed stages found for this period.</span>
                </div>
              ) : (
                <div className="overflow-x-auto mobile-scroll-table">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100">
                        <th className="text-left px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Stage</th>
                        <th className="text-right px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Expected</th>
                        <th className="text-right px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Actual</th>
                        <th className="text-right px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Avg Delay</th>
                        <th className="text-right px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">On-Time %</th>
                        <th className="text-right px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Runs</th>
                        <th className="text-center px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">SLA Bar</th>
                        <th className="text-center px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Trend</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {delayReport.map((row, i) => {
                        const isGood = row.on_time_pct >= 80;
                        const isMedium = row.on_time_pct >= 60 && row.on_time_pct < 80;
                        const statusColor = isGood
                          ? 'text-emerald-700 bg-emerald-50'
                          : isMedium
                            ? 'text-amber-700 bg-amber-50'
                            : 'text-rose-700 bg-rose-50';
                        const barColor = isGood ? 'bg-emerald-500' : isMedium ? 'bg-amber-500' : 'bg-rose-500';
                        return (
                          <tr key={i} className="hover:bg-slate-50/60 transition-colors group">
                            <td className="px-6 py-4">
                              <div className="font-bold text-slate-800 text-sm">{row.stage_name || row.stage_code}</div>
                              <div className="text-[10px] font-mono text-slate-400 mt-0.5">{row.stage_code}</div>
                            </td>
                            <td className="px-4 py-4 text-right font-mono text-slate-600 text-sm">
                              {row.avg_expected_mins}m
                            </td>
                            <td className="px-4 py-4 text-right font-mono text-slate-600 text-sm">
                              <span className={row.avg_actual_mins > row.avg_expected_mins ? 'text-rose-600 font-bold' : ''}>
                                {row.avg_actual_mins}m
                              </span>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <span className={`font-black text-sm ${row.avg_delay_mins > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                {row.avg_delay_mins > 0 ? `+${row.avg_delay_mins}m` : '—'}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-black ${statusColor}`}>
                                {row.on_time_pct}%
                              </span>
                            </td>
                            <td className="px-4 py-4 text-right font-mono text-slate-500 text-sm">
                              {row.total_count}
                            </td>
                            <td className="px-4 py-4">
                              <div className="w-28 mx-auto">
                                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all duration-700 ${barColor}`}
                                    style={{ width: `${Math.min(row.on_time_pct, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-center">
                              {isGood ? (
                                <TrendingUp size={18} className="mx-auto text-emerald-500" />
                              ) : (
                                <TrendingDown size={18} className="mx-auto text-rose-500" />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Legend */}
              {!delayLoading && delayReport.length > 0 && (
                <div className="px-6 py-3 border-t border-slate-100 flex items-center gap-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> ≥ 80% on-time</span>
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> 60–79% on-time</span>
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-500 inline-block" /> &lt; 60% on-time</span>
                </div>
              )}
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
// --- END OF MAIN DASHBOARD ---


